// Authentification globale des requêtes (Lot F — palier 2a).
//
// Le desktop a un login (JWT { id, role }) mais, historiquement, la plupart des
// appels partaient en `axios` nu SANS jeton, et l'API ne le vérifiait pas. Ce
// module enregistre, UNE fois au démarrage (importé dans index.js), deux
// intercepteurs sur l'instance axios par défaut — donc appliqués à TOUS les
// appels `import axios from 'axios'` de l'app, sans toucher chaque fichier :
//
//  - requête  : injecte « token: Bearer <jwt> », relu À CHAQUE requête depuis
//               le store Redux en mémoire (donc à jour dès le dispatch
//               loginSuccess, sans attendre le flush asynchrone de
//               redux-persist vers localStorage).
//  - réponse  : sur 401 (non authentifié / jeton expiré) → purge la session et
//               renvoie vers /login. On NE touche PAS au 403 (authentifié mais
//               non autorisé : géré localement, ex. action réservée admin).
import axios from 'axios';
import { store } from './redux/store';

// Lit le jeton courant. SOURCE DE VÉRITÉ = le store Redux en mémoire, mis à
// jour SYNCHRONEMENT par dispatch(loginSuccess). On NE lit PAS localStorage en
// premier : redux-persist y écrit de façon ASYNCHRONE (setInterval throttle),
// donc juste après un login le jeton n'y est pas encore. Lire localStorage à
// ce moment renvoyait null → requête sans header → 401 → l'intercepteur de
// réponse purgeait la session → déconnexion immédiate après la connexion.
// localStorage ne sert plus que de repli défensif (ex. store non encore prêt).
const readToken = () => {
  try {
    const state = store.getState();
    const fromStore =
      state && state.user && state.user.currentUser && state.user.currentUser.accessToken;
    if (fromStore) return fromStore;
  } catch (e) { /* ignore, on tente le repli */ }
  try {
    const root = JSON.parse(localStorage.getItem('persist:root'));
    const user = root && JSON.parse(root.user);
    return (user && user.currentUser && user.currentUser.accessToken) || null;
  } catch (e) {
    return null;
  }
};

axios.interceptors.request.use((cfg) => {
  const token = readToken();
  if (token) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.token) cfg.headers.token = `Bearer ${token}`;
  }
  return cfg;
});

let redirecting = false;
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err && err.response && err.response.status;
    const url = (err && err.config && err.config.url) || '';
    const isAuthCall = url.indexOf('/api/auth/') !== -1;
    if (status === 401 && !isAuthCall && !redirecting) {
      redirecting = true; // évite les redirections multiples si plusieurs appels échouent
      try { localStorage.removeItem('persist:root'); } catch (e) { /* ignore */ }
      if (typeof window !== 'undefined') {
        // APK Capacitor (REACT_APP_ROUTER=hash, origine capacitor://, build figé
        // SANS fallback SPA) : assign('/login') demanderait le fichier nu
        // capacitor://localhost/login → ERR_FILE_NOT_FOUND → écran blanc et app
        // bloquée à CHAQUE expiration de session. De plus, sous hash router
        // window.location.pathname vaut toujours '/'. On ne touche donc QUE le
        // fragment : le hash router rend /login sans navigation document.
        if (process.env.REACT_APP_ROUTER === 'hash') {
          if (!window.location.hash.startsWith('#/login')) window.location.assign('#/login');
        } else if (window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(err);
  },
);
